import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  ArrowForwardRounded,
  CallSplitRounded,
  DeleteRounded,
  EditRounded,
  SaveRounded,
} from '@mui/icons-material';
import {
  createIndustrialWorkflowStageTransition,
  deleteIndustrialWorkflowStageTransition,
  getIndustrialWorkflowStageAutomation,
  listIndustrialWorkflowStageTransitions,
  updateIndustrialWorkflowStageAutomation,
  updateIndustrialWorkflowStageTransition,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowStageAutomationUpsertPayload,
  IndustrialWorkflowStageDefinition,
  IndustrialWorkflowTransitionDefinition,
  IndustrialWorkflowTransitionDefinitionUpsertPayload,
} from '../types/industrialWorkflows';

type Props = {
  processDefinitionId: string;
  stages: IndustrialWorkflowStageDefinition[];
  initialStageId?: string;
  hideStageSelector?: boolean;
};

type FormState = {
  label: string;
  to_stage_definition_id: string;
  is_active: boolean;
  display_order: string;
  settings: Record<string, unknown>;
};

const EMPTY_FORM: FormState = {
  label: '',
  to_stage_definition_id: '',
  is_active: true,
  display_order: '0',
  settings: {},
};

function parseInteger(
  value: string,
  fallback = 0,
) {
  const parsedValue =
    Number.parseInt(value, 10);

  return Number.isFinite(parsedValue)
    ? parsedValue
    : fallback;
}

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function buildPayload(
  form: FormState,
): IndustrialWorkflowTransitionDefinitionUpsertPayload {
  return {
    to_stage_definition_id:
      form.to_stage_definition_id,

    label: form.label.trim(),

    auto_advance: false,

    is_active:
      form.is_active,

    display_order:
      parseInteger(
        form.display_order,
      ),

    settings:
      form.settings,
  };
}

export default function IndustrialWorkflowsStageTransitionsConfigSection({
  processDefinitionId,
  stages,
  initialStageId = '',
  hideStageSelector = false,
}: Props) {
  const queryClient =
    useQueryClient();

  const { enqueueSnackbar } =
    useSnackbar();

  const activeStages =
    useMemo(
      () =>
        stages.filter(
          (stage) =>
            stage.is_active,
        ),
      [stages],
    );

  const stageNameById =
    useMemo(
      () =>
        new Map(
          stages.map(
            (stage) => [
              stage.id,
              stage.name,
            ],
          ),
        ),
      [stages],
    );

  const [
    selectedStageId,
    setSelectedStageId,
  ] = useState(initialStageId);

  const [
    dialogOpen,
    setDialogOpen,
  ] = useState(false);

  const [
    editingTransition,
    setEditingTransition,
  ] =
    useState<IndustrialWorkflowTransitionDefinition | null>(
      null,
    );

  const [
    form,
    setForm,
  ] =
    useState<FormState>(
      EMPTY_FORM,
    );

  const [
    transitionPendingDelete,
    setTransitionPendingDelete,
  ] =
    useState<IndustrialWorkflowTransitionDefinition | null>(
      null,
    );

  useEffect(() => {
    if (!activeStages.length) {
      setSelectedStageId('');
      return;
    }

    const stillAvailable =
      activeStages.some(
        (stage) =>
          stage.id
          === selectedStageId,
      );

    if (!stillAvailable) {
      setSelectedStageId(
        activeStages[0].id,
      );
    }
  }, [
    activeStages,
    selectedStageId,
  ]);

  const selectedStage =
    useMemo(
      () =>
        stages.find(
          (stage) =>
            stage.id
            === selectedStageId,
        ) ?? null,
      [
        selectedStageId,
        stages,
      ],
    );

  const [
    habitualStageId,
    setHabitualStageId,
  ] = useState('');

  const automationQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-stage-automation',
        processDefinitionId,
        selectedStageId,
      ],
      queryFn: () =>
        getIndustrialWorkflowStageAutomation(
          processDefinitionId,
          selectedStageId,
        ),
      enabled: Boolean(
        processDefinitionId
        && selectedStageId,
      ),
      retry: false,
    });

  useEffect(() => {
    setHabitualStageId(
      automationQuery.data
        ?.next_stage_definition_id
      ?? '',
    );
  }, [
    automationQuery.data,
  ]);

  const transitionsQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-stage-transitions',
        processDefinitionId,
        selectedStageId,
        true,
      ],
      queryFn: () =>
        listIndustrialWorkflowStageTransitions(
          processDefinitionId,
          selectedStageId,
          true,
        ),
      enabled: Boolean(
        processDefinitionId
        && selectedStageId,
      ),
      retry: false,
    });

  const customTransitions =
    useMemo(
      () =>
        (
          transitionsQuery.data ?? []
        ).filter(
          (transition) =>
            transition.transition_key
              .toUpperCase()
            !== 'DEFAULT',
        ),
      [transitionsQuery.data],
    );

  const saveHabitualMutation =
    useMutation({
      mutationFn: (
        payload:
          IndustrialWorkflowStageAutomationUpsertPayload,
      ) =>
        updateIndustrialWorkflowStageAutomation(
          processDefinitionId,
          selectedStageId,
          payload,
        ),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [
              'industrial-workflows-stage-automation',
              processDefinitionId,
              selectedStageId,
            ],
          }),
          queryClient.invalidateQueries({
            queryKey: [
              'industrial-workflows-stage-transitions',
              processDefinitionId,
              selectedStageId,
            ],
          }),
        ]);

        enqueueSnackbar(
          'Salida habitual guardada correctamente.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const createMutation =
    useMutation({
      mutationFn: (
        payload:
          IndustrialWorkflowTransitionDefinitionUpsertPayload,
      ) =>
        createIndustrialWorkflowStageTransition(
          processDefinitionId,
          selectedStageId,
          payload,
        ),
      onSuccess: async () => {
        await queryClient
          .invalidateQueries({
            queryKey: [
              'industrial-workflows-stage-transitions',
              processDefinitionId,
              selectedStageId,
            ],
          });

        setDialogOpen(false);
        setEditingTransition(null);
        setForm(EMPTY_FORM);

        enqueueSnackbar(
          'Salida creada correctamente.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const updateMutation =
    useMutation({
      mutationFn: ({
        transitionDefinitionId,
        payload,
      }: {
        transitionDefinitionId: string;
        payload:
          IndustrialWorkflowTransitionDefinitionUpsertPayload;
      }) =>
        updateIndustrialWorkflowStageTransition(
          processDefinitionId,
          selectedStageId,
          transitionDefinitionId,
          payload,
        ),
      onSuccess: async () => {
        await queryClient
          .invalidateQueries({
            queryKey: [
              'industrial-workflows-stage-transitions',
              processDefinitionId,
              selectedStageId,
            ],
          });

        setDialogOpen(false);
        setEditingTransition(null);
        setForm(EMPTY_FORM);

        enqueueSnackbar(
          'Salida actualizada correctamente.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const deleteMutation =
    useMutation({
      mutationFn: (
        transitionDefinitionId: string,
      ) =>
        deleteIndustrialWorkflowStageTransition(
          processDefinitionId,
          selectedStageId,
          transitionDefinitionId,
        ),
      onSuccess: async () => {
        await queryClient
          .invalidateQueries({
            queryKey: [
              'industrial-workflows-stage-transitions',
              processDefinitionId,
              selectedStageId,
            ],
          });

        setTransitionPendingDelete(null);
        setDialogOpen(false);
        setEditingTransition(null);
        setForm(EMPTY_FORM);

        enqueueSnackbar(
          'Salida eliminada correctamente.',
          {
            variant: 'success',
          },
        );
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const handleCreate = () => {
    setEditingTransition(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const handleEdit = (
    transition:
      IndustrialWorkflowTransitionDefinition,
  ) => {
    setEditingTransition(
      transition,
    );

    setForm({
      label:
        transition.label ?? '',

      to_stage_definition_id:
        transition
          .to_stage_definition_id,

      is_active:
        transition.is_active,

      display_order:
        String(
          transition.display_order,
        ),

      settings:
        transition.settings ?? {},
    });

    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (
      createMutation.isPending
      || updateMutation.isPending
      || deleteMutation.isPending
    ) {
      return;
    }

    setDialogOpen(false);
    setEditingTransition(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = () => {
    if (!selectedStageId) {
      enqueueSnackbar(
        'Selecciona primero una etapa.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    if (!form.label.trim()) {
      enqueueSnackbar(
        'Indica una etiqueta visible para la salida.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    if (
      !form.to_stage_definition_id
    ) {
      enqueueSnackbar(
        'Selecciona una etapa de destino.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    const payload =
      buildPayload(form);

    if (editingTransition) {
      updateMutation.mutate({
        transitionDefinitionId:
          editingTransition.id,
        payload,
      });

      return;
    }

    createMutation.mutate(
      payload,
    );
  };

  const handleSaveHabitual = () => {
    const automation =
      automationQuery.data;

    if (!automation) {
      enqueueSnackbar(
        'Todavía no se ha podido cargar la configuración de la etapa.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    saveHabitualMutation.mutate({
      task_enabled:
        automation.task_enabled,

      task_title:
        automation.task_title,

      task_description:
        automation.task_description,

      work_area_id:
        automation.work_area_id,

      priority:
        automation.priority,

      due_offset_days:
        automation.due_offset_days,

      is_blocking:
        automation.is_blocking,

      next_stage_definition_id:
        habitualStageId || null,

      auto_advance: false,
    });
  };

  const handleConfirmDelete = () => {
    if (!transitionPendingDelete) {
      return;
    }

    deleteMutation.mutate(
      transitionPendingDelete.id,
    );
  };

  const mutationIsPending =
    createMutation.isPending
    || updateMutation.isPending
    || deleteMutation.isPending;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${alpha(
          '#38bdf8',
          0.2,
        )}`,
        bgcolor:
          'rgba(15,23,42,0.66)',
        px: {
          xs: 1.6,
          md: 2,
        },
        py: 1.7,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'flex-start',
            md: 'center',
          }}
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <CallSplitRounded
              sx={{
                color: '#7dd3fc',
              }}
            />

            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                }}
              >
                Salidas predefinidas
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                  mt: 0.2,
                }}
              >
                Define las opciones que se mostrarán al usuario
                cuando finalice esta etapa.
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<AddRounded />}
            disabled={
              !selectedStageId
              || Boolean(
                selectedStage
                  ?.is_final,
              )
            }
            onClick={
              handleCreate
            }
            sx={{
              textTransform: 'none',
              fontWeight: 900,
            }}
          >
            Nueva salida
          </Button>
        </Stack>

        <Divider
          sx={{
            borderColor: alpha(
              '#94a3b8',
              0.12,
            ),
          }}
        />

        {!activeStages.length ? (
          <Alert
            severity="info"
            variant="outlined"
          >
            Crea al menos una etapa activa para configurar
            sus salidas.
          </Alert>
        ) : (
          <>
            {!hideStageSelector ? (
              <TextField
                select
                size="small"
                label="Etapa a configurar"
                value={
                  selectedStageId
                }
                onChange={(event) =>
                  setSelectedStageId(
                    event.target.value,
                  )
                }
                fullWidth
              >
                {activeStages.map(
                  (stage) => (
                    <MenuItem
                      key={stage.id}
                      value={stage.id}
                    >
                      {stage.name}
                    </MenuItem>
                  ),
                )}
              </TextField>
            ) : null}

            {selectedStage?.is_final ? (
              <Alert
                severity="info"
                variant="outlined"
              >
                Esta es una etapa final. No puede disponer
                de salidas activas.
              </Alert>
            ) : null}

            {transitionsQuery.isFetching ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <CircularProgress
                  size={18}
                />

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.82rem',
                  }}
                >
                  Cargando salidas...
                </Typography>
              </Stack>
            ) : null}

            {transitionsQuery.isError ? (
              <Alert
                severity="error"
                variant="outlined"
              >
                {getErrorMessage(
                  transitionsQuery.error,
                )}
              </Alert>
            ) : null}

            <Stack spacing={0.8}>
              <Typography
                sx={{
                  color: '#cbd5e1',
                  fontWeight: 900,
                  fontSize: '0.8rem',
                  letterSpacing: 0.65,
                  textTransform:
                    'uppercase',
                }}
              >
                Salida habitual
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.8rem',
                }}
              >
                Esta opción aparecerá seleccionada inicialmente al
                finalizar la etapa. El expediente no se moverá hasta
                que el usuario confirme su elección.
              </Typography>

              {automationQuery.isError ? (
                <Alert
                  severity="error"
                  variant="outlined"
                >
                  {getErrorMessage(
                    automationQuery.error,
                  )}
                </Alert>
              ) : null}

              {selectedStage?.is_final ? (
                <Alert
                  severity="info"
                  variant="outlined"
                >
                  Las etapas finales no disponen de una salida habitual.
                </Alert>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(
                      '#a78bfa',
                      0.2,
                    )}`,
                    bgcolor:
                      'rgba(2,6,23,0.22)',
                    px: 1.35,
                    py: 1.15,
                  }}
                >
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'row',
                    }}
                    spacing={1}
                    alignItems={{
                      xs: 'stretch',
                      sm: 'center',
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label="Etapa habitual de destino"
                      value={habitualStageId}
                      onChange={(event) =>
                        setHabitualStageId(
                          event.target.value,
                        )
                      }
                      disabled={
                        automationQuery.isFetching
                      }
                      fullWidth
                    >
                      <MenuItem value="">
                        Sin salida habitual
                      </MenuItem>

                      {activeStages
                        .filter(
                          (stage) =>
                            stage.id
                            !== selectedStageId,
                        )
                        .map(
                          (stage) => (
                            <MenuItem
                              key={stage.id}
                              value={stage.id}
                            >
                              {stage.name}
                            </MenuItem>
                          ),
                        )}
                    </TextField>

                    <Button
                      variant="outlined"
                      startIcon={<SaveRounded />}
                      disabled={
                        automationQuery.isFetching
                        || saveHabitualMutation.isPending
                      }
                      onClick={
                        handleSaveHabitual
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 900,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Guardar salida
                    </Button>
                  </Stack>
                </Paper>
              )}
            </Stack>

            <Divider
              sx={{
                borderColor: alpha(
                  '#94a3b8',
                  0.12,
                ),
              }}
            />

            <Stack spacing={0.8}>
              <Typography
                sx={{
                  color: '#cbd5e1',
                  fontWeight: 900,
                  fontSize: '0.8rem',
                  letterSpacing: 0.65,
                  textTransform:
                    'uppercase',
                }}
              >
                Otras salidas previstas
              </Typography>

              {!customTransitions.length ? (
                <Alert
                  severity="info"
                  variant="outlined"
                >
                  Todavía no hay otras salidas previstas.
                </Alert>
              ) : null}

              {customTransitions.map(
                (transition) => (
                  <Paper
                    key={
                      transition.id
                    }
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(
                        transition
                          .is_active
                          ? '#38bdf8'
                          : '#64748b',
                        0.18,
                      )}`,
                      bgcolor:
                        'rgba(2,6,23,0.22)',
                      px: 1.35,
                      py: 1.15,
                    }}
                  >
                    <Stack
                      direction={{
                        xs: 'column',
                        sm: 'row',
                      }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Stack
                        direction="row"
                        spacing={0.9}
                        alignItems="center"
                      >
                        <ArrowForwardRounded
                          sx={{
                            color:
                              transition
                                .is_active
                                ? '#7dd3fc'
                                : '#64748b',
                          }}
                        />

                        <Box>
                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 900,
                            }}
                          >
                            {transition.label
                              ?? 'Salida sin etiqueta'}
                          </Typography>

                          <Typography
                            sx={{
                              color: '#94a3b8',
                              fontSize: '0.78rem',
                              mt: 0.25,
                            }}
                          >
                            Hacia{' '}
                            {stageNameById.get(
                              transition
                                .to_stage_definition_id,
                            ) ?? 'etapa no disponible'}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={0.65}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >

                        {!transition
                          .is_active ? (
                          <Chip
                            size="small"
                            label="Inactiva"
                            sx={{
                              color: '#cbd5e1',
                              bgcolor: alpha(
                                '#64748b',
                                0.14,
                              ),
                            }}
                          />
                        ) : null}

                        <IconButton
                          size="small"
                          aria-label="Editar salida"
                          onClick={() =>
                            handleEdit(
                              transition,
                            )
                          }
                          sx={{
                            color: '#94a3b8',
                          }}
                        >
                          <EditRounded
                            fontSize="small"
                          />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                ),
              )}
            </Stack>
          </>
        )}
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={
          handleCloseDialog
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingTransition
            ? 'Editar salida'
            : 'Nueva salida'}
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={2}
            sx={{
              pt: 1,
            }}
          >
            <TextField
              label="Etiqueta visible"
              value={
                form.label
              }
              onChange={(event) =>
                setForm(
                  (current) => ({
                    ...current,
                    label:
                      event.target.value,
                  }),
                )
              }
              helperText="Ejemplo: Oferta aceptada, Solicitar revisión o Proyecto cancelado."
              fullWidth
              required
            />

            <TextField
              select
              label="Etapa de destino"
              value={
                form.to_stage_definition_id
              }
              onChange={(event) =>
                setForm(
                  (current) => ({
                    ...current,
                    to_stage_definition_id:
                      event.target.value,
                  }),
                )
              }
              fullWidth
              required
            >
              {stages
                .filter(
                  (stage) =>
                    stage.id
                    !== selectedStageId,
                )
                .map(
                  (stage) => (
                    <MenuItem
                      key={stage.id}
                      value={stage.id}
                    >
                      {stage.name}
                      {stage.is_active
                        ? ''
                        : ' · Inactiva'}
                    </MenuItem>
                  ),
                )}
            </TextField>

            <TextField
              label="Orden visual"
              value={
                form.display_order
              }
              onChange={(event) =>
                setForm(
                  (current) => ({
                    ...current,
                    display_order:
                      event.target.value,
                  }),
                )
              }
              type="number"
              fullWidth
            />

            <Stack spacing={0.25}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      form.is_active
                    }
                    onChange={(event) =>
                      setForm(
                        (current) => ({
                          ...current,
                          is_active:
                            event.target
                              .checked,
                        }),
                      )
                    }
                  />
                }
                label="Salida activa"
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: 1 }}
          >
            {editingTransition ? (
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteRounded />}
                disabled={mutationIsPending}
                onClick={() =>
                  setTransitionPendingDelete(
                    editingTransition,
                  )
                }
              >
                Eliminar salida
              </Button>
            ) : (
              <Box />
            )}

            <Stack
              direction="row"
              spacing={1}
            >
              <Button
                onClick={
                  handleCloseDialog
                }
                disabled={
                  mutationIsPending
                }
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                disabled={
                  mutationIsPending
                }
                onClick={
                  handleSave
                }
              >
                Guardar
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(transitionPendingDelete)}
        onClose={() => {
          if (!deleteMutation.isPending) {
            setTransitionPendingDelete(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Eliminar salida prevista
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={1.5}
            sx={{ pt: 1 }}
          >
            <Typography>
              Vas a eliminar la salida{' '}
              <strong>
                {transitionPendingDelete?.label}
              </strong>
              .
            </Typography>

            <Alert
              severity="warning"
              variant="outlined"
            >
              La salida dejará de mostrarse cuando se finalice
              esta etapa. Los movimientos ya registrados
              conservarán su información histórica.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={deleteMutation.isPending}
            onClick={() =>
              setTransitionPendingDelete(null)
            }
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteRounded />}
            disabled={deleteMutation.isPending}
            onClick={handleConfirmDelete}
          >
            Confirmar eliminación
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}